/* ─────────────────────────────────────────────────────────────────────────
 * Cutout — the desk.
 *
 * Every object on the mat is DRAWN, not photographed: hand-built SVG seen from
 * directly above, the way a flat-lay sees it. That buys three things a folder
 * of cut-out PNGs can't:
 *
 *   • nothing to download — the whole desk is a few kB of markup, so the
 *     section's only real payload is the photography it is selling;
 *   • every object is a live DOM node, so the cursor can shove it and the
 *     rAF loop can write its transform without touching a texture;
 *   • the shadows are ours. One light source, up and to the left, for every
 *     object on the desk — which is the single thing that makes a flat-lay
 *     read as one photograph rather than a pile of stickers.
 *
 * Each component draws in its own viewBox and is sized by the caller. None of
 * them set a shadow — the stage does, so the whole desk stays lit alike.
 * ───────────────────────────────────────────────────────────────────────── */

import type { ReactNode } from "react";

interface PropSvgProps {
  /** Unique per instance — SVG gradient ids are document-global. */
  uid: string;
}

/** The desk's one light: up-left. Every metal highlight runs the same way. */
const METAL_LIGHT = "#FDFDFB";
const METAL_MID = "#C9CBC8";
const METAL_DARK = "#8A8D8B";

function Defs({ children }: { children: ReactNode }) {
  return <defs>{children}</defs>;
}

/* ── scissors ────────────────────────────────────────────────────────────
 * The hero prop: it's the one the section actually uses (it flies in and
 * cuts the chosen word out of its paper). Blades crossed at a rivet,
 * handles below, seen from above at a slight open angle. */
export function Scissors({ uid }: PropSvgProps) {
  return (
    <svg viewBox="0 0 120 200" width="100%" height="100%" fill="none">
      <Defs>
        <linearGradient id={`${uid}-blade`} x1="0" y1="0" x2="1" y2="0.3">
          <stop offset="0" stopColor={METAL_LIGHT} />
          <stop offset="0.42" stopColor="#E6E8E5" />
          <stop offset="0.62" stopColor={METAL_MID} />
          <stop offset="1" stopColor={METAL_DARK} />
        </linearGradient>
        <linearGradient id={`${uid}-grip`} x1="0" y1="0" x2="0.7" y2="1">
          <stop offset="0" stopColor="#3E6FD4" />
          <stop offset="0.5" stopColor="#2C55B0" />
          <stop offset="1" stopColor="#1D3C86" />
        </linearGradient>
      </Defs>
      {/* blades — long tapered leaves crossing at the rivet */}
      <path
        d="M60 96 L44 20 Q42 8 50 6 Q58 5 60 16 L66 94 Z"
        fill={`url(#${uid}-blade)`}
        stroke="#7E817F"
        strokeWidth="1.1"
      />
      <path
        d="M60 96 L76 20 Q78 8 70 6 Q62 5 60 16 L54 94 Z"
        fill={`url(#${uid}-blade)`}
        stroke="#7E817F"
        strokeWidth="1.1"
      />
      {/* the cutting edge catches the light hardest */}
      <path d="M50 12 L59 92" stroke="#FFFFFF" strokeWidth="1.6" opacity="0.85" />
      <path d="M70 12 L61 92" stroke="#FFFFFF" strokeWidth="1.1" opacity="0.5" />
      {/* shanks into the grips */}
      <path d="M60 96 L46 132" stroke="#B9BCBA" strokeWidth="7" strokeLinecap="round" />
      <path d="M60 96 L74 132" stroke="#B9BCBA" strokeWidth="7" strokeLinecap="round" />
      {/* grips */}
      <path
        d="M46 128 Q22 138 24 160 Q26 184 50 182 Q68 180 64 158 Q61 140 52 132 Z"
        fill={`url(#${uid}-grip)`}
      />
      <path
        d="M74 128 Q98 138 96 160 Q94 184 70 182 Q52 180 56 158 Q59 140 68 132 Z"
        fill={`url(#${uid}-grip)`}
      />
      <ellipse cx="42" cy="158" rx="12" ry="15" fill="#1B1D22" opacity="0.9" />
      <ellipse cx="78" cy="158" rx="12" ry="15" fill="#1B1D22" opacity="0.9" />
      <path d="M32 146 Q28 158 32 170" stroke="#7FA0EC" strokeWidth="2.5" opacity="0.7" />
      <path d="M88 146 Q92 158 88 170" stroke="#7FA0EC" strokeWidth="2.5" opacity="0.5" />
      {/* rivet */}
      <circle cx="60" cy="96" r="7" fill="#D8DAD7" stroke="#8A8D8B" strokeWidth="1.2" />
      <circle cx="58" cy="94" r="2.4" fill="#FFFFFF" opacity="0.9" />
    </svg>
  );
}

/* ── binder clip, seen from above ─────────────────────────────────────── */
export function BinderClip({ uid }: PropSvgProps) {
  return (
    <svg viewBox="0 0 100 78" width="100%" height="100%" fill="none">
      <Defs>
        <linearGradient id={`${uid}-body`} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor="#4A4C50" />
          <stop offset="0.35" stopColor="#232529" />
          <stop offset="1" stopColor="#111214" />
        </linearGradient>
      </Defs>
      {/* the two wire arms, folded back */}
      <path
        d="M30 30 Q14 12 26 8 Q38 5 44 24"
        stroke="#B7BAB8"
        strokeWidth="4.5"
        strokeLinecap="round"
      />
      <path
        d="M70 30 Q86 12 74 8 Q62 5 56 24"
        stroke="#9FA3A1"
        strokeWidth="4.5"
        strokeLinecap="round"
      />
      {/* body */}
      <path d="M18 30 H82 L68 70 H32 Z" fill={`url(#${uid}-body)`} />
      <path d="M18 30 H82 L79 38 H21 Z" fill="#5C5F64" opacity="0.55" />
      <path d="M24 33 L36 66" stroke="#000" strokeWidth="1" opacity="0.35" />
    </svg>
  );
}

/* ── paperclip ───────────────────────────────────────────────────────── */
export function Paperclip({ uid }: PropSvgProps) {
  return (
    <svg viewBox="0 0 46 110" width="100%" height="100%" fill="none">
      <Defs>
        <linearGradient id={`${uid}-wire`} x1="0" y1="0" x2="1" y2="0.6">
          <stop offset="0" stopColor="#7FB2F0" />
          <stop offset="0.45" stopColor="#3E7BD4" />
          <stop offset="1" stopColor="#24509B" />
        </linearGradient>
      </Defs>
      {/* The real thing is one wire folded three times, and it only reads as
          a paperclip if all three passes are visible as separate strokes —
          drawn as one path they merge into a bar at this size. */}
      <path
        d="M11 84 V24 Q11 8 23 8 Q35 8 35 24 V88"
        stroke={`url(#${uid}-wire)`}
        strokeWidth="5"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M35 88 Q35 100 24 100 Q13 100 13 88 V34"
        stroke={`url(#${uid}-wire)`}
        strokeWidth="5"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M23 90 V38"
        stroke={`url(#${uid}-wire)`}
        strokeWidth="5"
        strokeLinecap="round"
        fill="none"
      />
      <path d="M13 26 Q13 13 23 13" stroke="#CFE4FB" strokeWidth="1.8" fill="none" opacity="0.85" />
    </svg>
  );
}

/* ── reading glasses, folded, seen from above ────────────────────────── */
export function Glasses({ uid }: PropSvgProps) {
  return (
    <svg viewBox="0 0 210 90" width="100%" height="100%" fill="none">
      <Defs>
        <linearGradient id={`${uid}-shell`} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0" stopColor="#B07A3F" />
          <stop offset="0.4" stopColor="#7A4A1E" />
          <stop offset="1" stopColor="#472911" />
        </linearGradient>
        <radialGradient id={`${uid}-lens`} cx="0.35" cy="0.3" r="0.85">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.5" />
          <stop offset="0.6" stopColor="#D9E3E0" stopOpacity="0.18" />
          <stop offset="1" stopColor="#8FA5A0" stopOpacity="0.1" />
        </radialGradient>
      </Defs>
      {/* arms, folded across */}
      <path
        d="M52 52 Q28 66 12 78"
        stroke={`url(#${uid}-shell)`}
        strokeWidth="7"
        strokeLinecap="round"
      />
      <path
        d="M158 52 Q182 66 198 78"
        stroke={`url(#${uid}-shell)`}
        strokeWidth="7"
        strokeLinecap="round"
      />
      {/* rims */}
      <ellipse cx="66" cy="40" rx="42" ry="30" fill={`url(#${uid}-lens)`} stroke={`url(#${uid}-shell)`} strokeWidth="8" />
      <ellipse cx="144" cy="40" rx="42" ry="30" fill={`url(#${uid}-lens)`} stroke={`url(#${uid}-shell)`} strokeWidth="8" />
      <path d="M108 34 Q105 28 102 34" stroke={`url(#${uid}-shell)`} strokeWidth="6" strokeLinecap="round" />
      {/* the glass throws a highlight the mat can see */}
      <path d="M44 26 Q58 16 76 20" stroke="#FFFFFF" strokeWidth="3" opacity="0.55" strokeLinecap="round" />
      <path d="M122 26 Q136 16 154 20" stroke="#FFFFFF" strokeWidth="3" opacity="0.4" strokeLinecap="round" />
      {/* tortoiseshell flecks */}
      <ellipse cx="40" cy="52" rx="5" ry="3" fill="#2A170A" opacity="0.55" />
      <ellipse cx="94" cy="20" rx="4" ry="2.5" fill="#2A170A" opacity="0.45" />
      <ellipse cx="176" cy="46" rx="5" ry="3" fill="#2A170A" opacity="0.5" />
    </svg>
  );
}

/* ── pen ─────────────────────────────────────────────────────────────── */
export function Pen({ uid, hue = "#D8362F" }: PropSvgProps & { hue?: string }) {
  return (
    <svg viewBox="0 0 34 260" width="100%" height="100%" fill="none">
      <Defs>
        <linearGradient id={`${uid}-barrel`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.55" />
          <stop offset="0.22" stopColor={hue} />
          <stop offset="0.72" stopColor={hue} />
          <stop offset="1" stopColor="#000000" stopOpacity="0.45" />
        </linearGradient>
      </Defs>
      <rect x="8" y="14" width="18" height="228" rx="9" fill={hue} />
      <rect x="8" y="14" width="18" height="228" rx="9" fill={`url(#${uid}-barrel)`} />
      {/* tapered nib end */}
      <path d="M13 242 L17 258 L21 242 Z" fill="#2A2A2A" />
      {/* cap band + clip */}
      <rect x="7" y="14" width="20" height="74" rx="9" fill={hue} />
      <rect x="7" y="14" width="20" height="74" rx="9" fill={`url(#${uid}-barrel)`} />
      <rect x="7" y="86" width="20" height="5" rx="2" fill="#F0EDE6" opacity="0.85" />
      <path d="M24 22 Q30 24 30 34 V62 Q30 68 26 68" stroke="#F2EFE8" strokeWidth="3.4" strokeLinecap="round" fill="none" />
      <rect x="11" y="20" width="3.4" height="216" rx="1.7" fill="#FFFFFF" opacity="0.35" />
    </svg>
  );
}

/* ── craft knife ─────────────────────────────────────────────────────── */
export function CraftKnife({ uid }: PropSvgProps) {
  return (
    <svg viewBox="0 0 40 250" width="100%" height="100%" fill="none">
      <Defs>
        <linearGradient id={`${uid}-hilt`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#E9EBE8" />
          <stop offset="0.35" stopColor="#B9BCB9" />
          <stop offset="0.75" stopColor="#83878A" />
          <stop offset="1" stopColor="#5C6063" />
        </linearGradient>
        <linearGradient id={`${uid}-steel`} x1="0" y1="0" x2="1" y2="0.2">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="0.5" stopColor="#DADCD9" />
          <stop offset="1" stopColor="#9DA0A2" />
        </linearGradient>
      </Defs>
      <rect x="11" y="60" width="18" height="180" rx="7" fill={`url(#${uid}-hilt)`} />
      {/* knurling */}
      {Array.from({ length: 14 }, (_, i) => (
        <rect key={i} x="11" y={92 + i * 9} width="18" height="2.6" fill="#6E7275" opacity="0.4" />
      ))}
      <path d="M20 60 L28 46 L26 30 L20 8 L14 30 L12 46 Z" fill={`url(#${uid}-steel)`} stroke="#83878A" strokeWidth="0.8" />
      <path d="M20 10 L20 56" stroke="#FFFFFF" strokeWidth="1.6" opacity="0.9" />
      <rect x="11" y="56" width="18" height="8" rx="3" fill="#4E5255" />
    </svg>
  );
}

/* ── a strip of matte tape ───────────────────────────────────────────── */
export function Tape({ uid, tint = "#F3EEE0" }: PropSvgProps & { tint?: string }) {
  return (
    <svg viewBox="0 0 160 44" width="100%" height="100%" fill="none" preserveAspectRatio="none">
      <Defs>
        <linearGradient id={`${uid}-t`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.55" />
          <stop offset="0.4" stopColor={tint} stopOpacity="0.86" />
          <stop offset="1" stopColor={tint} stopOpacity="0.7" />
        </linearGradient>
      </Defs>
      {/* torn ends: a jagged run at each edge */}
      <path
        d="M4 6 L10 2 L16 7 L23 3 L30 8 L38 4 L46 8 L54 3 L62 7 L70 3 L78 8 L86 4 L94 8 L102 3 L110 7 L118 3 L126 8 L134 4 L142 8 L150 4 L156 8
           L156 36 L149 41 L141 36 L133 41 L125 36 L117 41 L109 36 L101 41 L93 36 L85 41 L77 36 L69 41 L61 36 L53 41 L45 36 L37 41 L29 36 L21 41 L13 36 L5 40 Z"
        fill={`url(#${uid}-t)`}
      />
      <path d="M6 14 H154" stroke="#FFFFFF" strokeWidth="1.2" opacity="0.4" />
    </svg>
  );
}

/* ── pushpin, top-down: a dome and the ring of its own shadow ────────── */
export function Pushpin({ uid, hue = "#E4C33C" }: PropSvgProps & { hue?: string }) {
  return (
    <svg viewBox="0 0 60 60" width="100%" height="100%" fill="none">
      <Defs>
        <radialGradient id={`${uid}-dome`} cx="0.34" cy="0.28" r="0.8">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.95" />
          <stop offset="0.22" stopColor={hue} />
          <stop offset="1" stopColor="#000000" stopOpacity="0.55" />
        </radialGradient>
      </Defs>
      <circle cx="30" cy="30" r="20" fill={hue} />
      <circle cx="30" cy="30" r="20" fill={`url(#${uid}-dome)`} />
      <circle cx="30" cy="30" r="7" fill="#000000" opacity="0.18" />
      <circle cx="23" cy="22" r="4.5" fill="#FFFFFF" opacity="0.75" />
    </svg>
  );
}

/* ── a paper star, cut by hand (so the points aren't equal) ──────────── */
export function PaperStar({ uid, tint = "#F6F2E8" }: PropSvgProps & { tint?: string }) {
  return (
    <svg viewBox="0 0 100 100" width="100%" height="100%" fill="none">
      <Defs>
        <linearGradient id={`${uid}-p`} x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="0.55" stopColor={tint} />
          <stop offset="1" stopColor="#D9D2C2" />
        </linearGradient>
      </Defs>
      <path
        d="M50 4 L61 36 L96 37 L68 58 L79 92 L50 71 L20 93 L31 58 L3 36 L39 35 Z"
        fill={`url(#${uid}-p)`}
      />
      {/* the fold the scissors left */}
      <path d="M50 4 L50 71" stroke="#000000" strokeWidth="0.8" opacity="0.09" />
      <path d="M39 35 L68 58" stroke="#000000" strokeWidth="0.8" opacity="0.06" />
    </svg>
  );
}

/* ── 35mm off-cut ────────────────────────────────────────────────────── */
export function FilmStrip({ uid, frames = 3 }: PropSvgProps & { frames?: number }) {
  const fw = 58;
  const w = frames * fw + 8;
  return (
    <svg viewBox={`0 0 ${w} 78`} width="100%" height="100%" fill="none">
      <Defs>
        <linearGradient id={`${uid}-base`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2B2722" />
          <stop offset="0.5" stopColor="#191612" />
          <stop offset="1" stopColor="#26221D" />
        </linearGradient>
      </Defs>
      <rect x="0" y="0" width={w} height="78" rx="2" fill={`url(#${uid}-base)`} />
      {Array.from({ length: frames }, (_, f) => (
        <g key={f}>
          <rect x={6 + f * fw} y="16" width={fw - 8} height="46" fill="#0B0908" />
          <rect x={6 + f * fw} y="16" width={fw - 8} height="46" fill="#6E5A46" opacity={0.25 + f * 0.12} />
        </g>
      ))}
      {/* sprockets, both edges */}
      {Array.from({ length: Math.floor(w / 13) }, (_, i) => (
        <g key={i}>
          <rect x={4 + i * 13} y="4" width="8" height="7" rx="1.6" fill="#F0EDE6" opacity="0.9" />
          <rect x={4 + i * 13} y="67" width="8" height="7" rx="1.6" fill="#F0EDE6" opacity="0.9" />
        </g>
      ))}
    </svg>
  );
}

/* ── loupe ───────────────────────────────────────────────────────────── */
export function Loupe({ uid }: PropSvgProps) {
  return (
    <svg viewBox="0 0 130 130" width="100%" height="100%" fill="none">
      <Defs>
        <linearGradient id={`${uid}-ring`} x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0" stopColor="#F2F3F1" />
          <stop offset="0.4" stopColor="#B7BAB7" />
          <stop offset="1" stopColor="#6E7274" />
        </linearGradient>
        <radialGradient id={`${uid}-glass`} cx="0.34" cy="0.28" r="0.85">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.34" />
          <stop offset="0.65" stopColor="#CBD8D4" stopOpacity="0.1" />
          <stop offset="1" stopColor="#6D807C" stopOpacity="0.16" />
        </radialGradient>
      </Defs>
      <circle cx="65" cy="65" r="56" fill="none" stroke={`url(#${uid}-ring)`} strokeWidth="11" />
      <circle cx="65" cy="65" r="50" fill={`url(#${uid}-glass)`} />
      <path d="M28 34 Q44 18 66 16" stroke="#FFFFFF" strokeWidth="4" opacity="0.5" strokeLinecap="round" fill="none" />
    </svg>
  );
}

/* ── eraser crumbs ───────────────────────────────────────────────────── */
export function Crumbs({ uid }: PropSvgProps) {
  /* Eraser shavings. Each one is a short, fat, tapered comma — a rubbing
   * leaves a curl with one thick end and one thin, and that taper is the
   * only thing that separates it from a grain of rice at 30px wide. */
  const bits: [number, number, number, number][] = [
    // x, y, length, rotation
    [10, 22, 22, -14],
    [30, 12, 18, 40],
    [46, 26, 16, -6],
    [21, 34, 14, 26],
    [56, 15, 12, 62],
  ];
  return (
    <svg viewBox="0 0 70 46" width="100%" height="100%" fill="none">
      <Defs>
        <linearGradient id={`${uid}-c`} x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0" stopColor="#FFFDF8" />
          <stop offset="0.55" stopColor="#EFE7D8" />
          <stop offset="1" stopColor="#C9C0AC" />
        </linearGradient>
      </Defs>
      {bits.map(([x, y, len, rot], i) => (
        <path
          key={i}
          d={`M0 0 q${len * 0.42} ${-len * 0.34} ${len} ${-len * 0.1}
              q${-len * 0.34} ${len * 0.34} ${-len} ${len * 0.28} z`}
          fill={`url(#${uid}-c)`}
          transform={`translate(${x} ${y}) rotate(${rot})`}
        />
      ))}
    </svg>
  );
}

/* ── the cursor from the reference: a fat pixel arrow ────────────────── */
export function PixelArrow({ uid }: PropSvgProps) {
  return (
    <svg viewBox="0 0 64 84" width="100%" height="100%" fill="none">
      <Defs>
        <linearGradient id={`${uid}-a`} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#D8D4CC" />
        </linearGradient>
      </Defs>
      <path
        d="M8 4 L8 66 L22 53 L32 78 L46 72 L36 48 L56 47 Z"
        fill={`url(#${uid}-a)`}
        stroke="#141414"
        strokeWidth="5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ── a wooden ruler along one edge ───────────────────────────────────── */
export function Ruler({ uid }: PropSvgProps) {
  return (
    <svg viewBox="0 0 520 46" width="100%" height="100%" fill="none" preserveAspectRatio="none">
      <Defs>
        <linearGradient id={`${uid}-wood`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F0D9A8" />
          <stop offset="0.35" stopColor="#E3C489" />
          <stop offset="1" stopColor="#C39F62" />
        </linearGradient>
      </Defs>
      <rect x="0" y="0" width="520" height="46" rx="3" fill={`url(#${uid}-wood)`} />
      <rect x="0" y="0" width="520" height="46" rx="3" fill="none" stroke="#A8834A" strokeWidth="1" />
      {Array.from({ length: 52 }, (_, i) => (
        <rect
          key={i}
          x={6 + i * 10}
          y="0"
          width="1.6"
          height={i % 5 === 0 ? 17 : 10}
          fill="#5E4522"
          opacity="0.8"
        />
      ))}
      <rect x="0" y="30" width="520" height="1" fill="#A8834A" opacity="0.5" />
    </svg>
  );
}

/* ── a torn magazine scrap ───────────────────────────────────────────── */
export function Scrap({ uid, tint = "#EFE7D6" }: PropSvgProps & { tint?: string }) {
  return (
    <svg viewBox="0 0 150 96" width="100%" height="100%" fill="none">
      <Defs>
        <linearGradient id={`${uid}-s`} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="1" stopColor={tint} />
        </linearGradient>
      </Defs>
      <path
        d="M4 10 L26 4 L52 9 L78 3 L104 10 L128 5 L146 12 L142 40 L147 66 L140 90 L112 86 L86 92 L58 85 L30 91 L8 84 L11 58 L3 34 Z"
        fill={`url(#${uid}-s)`}
      />
      {/* printed lines, unreadable at this size — which is the point */}
      <rect x="18" y="24" width="96" height="7" rx="3.5" fill="#2A2622" opacity="0.75" />
      <rect x="18" y="40" width="112" height="4" rx="2" fill="#2A2622" opacity="0.3" />
      <rect x="18" y="50" width="78" height="4" rx="2" fill="#2A2622" opacity="0.3" />
      <rect x="18" y="60" width="94" height="4" rx="2" fill="#2A2622" opacity="0.22" />
    </svg>
  );
}

/* ── coffee ring: flat on the mat, so the stage gives it no shadow ───── */
export function CoffeeRing({ uid }: PropSvgProps) {
  return (
    <svg viewBox="0 0 140 140" width="100%" height="100%" fill="none">
      <Defs>
        <radialGradient id={`${uid}-r`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0.72" stopColor="#6B4A2A" stopOpacity="0" />
          <stop offset="0.86" stopColor="#6B4A2A" stopOpacity="0.34" />
          <stop offset="0.95" stopColor="#4E3419" stopOpacity="0.42" />
          <stop offset="1" stopColor="#6B4A2A" stopOpacity="0" />
        </radialGradient>
      </Defs>
      <circle cx="70" cy="70" r="68" fill={`url(#${uid}-r)`} />
      <circle cx="70" cy="70" r="58" fill="#6B4A2A" opacity="0.05" />
    </svg>
  );
}

/* ── a lens cap, face down ───────────────────────────────────────────── */
export function LensCap({ uid }: PropSvgProps) {
  return (
    <svg viewBox="0 0 120 120" width="100%" height="100%" fill="none">
      <Defs>
        <radialGradient id={`${uid}-cap`} cx="0.35" cy="0.3" r="0.85">
          <stop offset="0" stopColor="#4A4C50" />
          <stop offset="0.6" stopColor="#232528" />
          <stop offset="1" stopColor="#0E0F11" />
        </radialGradient>
      </Defs>
      <circle cx="60" cy="60" r="54" fill={`url(#${uid}-cap)`} />
      <circle cx="60" cy="60" r="42" fill="none" stroke="#3A3D41" strokeWidth="2" />
      {Array.from({ length: 30 }, (_, i) => {
        const a = (i / 30) * Math.PI * 2;
        return (
          <rect
            key={i}
            x="59"
            y="6"
            width="2"
            height="9"
            rx="1"
            fill="#54585C"
            transform={`rotate(${(a * 180) / Math.PI} 60 60)`}
          />
        );
      })}
      <circle cx="60" cy="60" r="20" fill="#17181A" />
      <path d="M32 30 Q46 18 64 16" stroke="#7E838A" strokeWidth="3" opacity="0.4" strokeLinecap="round" fill="none" />
    </svg>
  );
}

/* ── a rubber band, dropped ──────────────────────────────────────────── */
export function RubberBand({ uid }: PropSvgProps) {
  return (
    <svg viewBox="0 0 120 90" width="100%" height="100%" fill="none">
      <path
        d="M14 44 Q6 20 34 14 Q70 6 98 20 Q118 30 106 52 Q92 76 56 78 Q22 80 14 60 Q10 48 26 42"
        stroke="#C98B96"
        strokeWidth="7"
        strokeLinecap="round"
        fill="none"
        opacity="0.95"
      />
      <path
        d="M20 40 Q14 24 36 19"
        stroke="#F0BFC7"
        strokeWidth="2.4"
        strokeLinecap="round"
        fill="none"
        opacity="0.8"
      />
      <Defs>
        <linearGradient id={`${uid}-rb`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#E7A9B4" />
          <stop offset="1" stopColor="#B4737F" />
        </linearGradient>
      </Defs>
    </svg>
  );
}

/* ── a stack of coloured card ────────────────────────────────────────── */
export function CardStack({ uid, tint = "#E9A0B4" }: PropSvgProps & { tint?: string }) {
  return (
    <svg viewBox="0 0 180 140" width="100%" height="100%" fill="none">
      <Defs>
        <linearGradient id={`${uid}-cs`} x1="0" y1="0" x2="0.5" y2="1">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.42" />
          <stop offset="0.5" stopColor={tint} />
          <stop offset="1" stopColor="#000000" stopOpacity="0.16" />
        </linearGradient>
      </Defs>
      <rect x="10" y="26" width="158" height="104" rx="3" fill={tint} opacity="0.55" transform="rotate(-3 89 78)" />
      <rect x="14" y="18" width="158" height="104" rx="3" fill={tint} opacity="0.75" transform="rotate(2 93 70)" />
      <rect x="8" y="10" width="158" height="104" rx="3" fill={`url(#${uid}-cs)`} transform="rotate(-1 87 62)" />
    </svg>
  );
}

export const DESK_PROPS = {
  Scissors,
  BinderClip,
  Paperclip,
  Glasses,
  Pen,
  CraftKnife,
  Tape,
  Pushpin,
  PaperStar,
  FilmStrip,
  Loupe,
  Crumbs,
  PixelArrow,
  Ruler,
  Scrap,
  CoffeeRing,
  LensCap,
  RubberBand,
  CardStack,
};
